import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";
import SEOHead from "@/components/base/SEOHead";
import { articleList } from "@/mocks/article-data";
import { articleContents } from "@/mocks/article-contents";

function findArticleBySlug(slug: string) {
  return articleContents[slug] || null;
}

function getRelatedTitles(slugs: string[]) {
  return slugs.map((s) => {
    const found = articleList.find((a) => a.slug === s);
    return found ? { slug: s, title: found.title } : null;
  }).filter(Boolean) as { slug: string; title: string }[];
}

function buildArticleJsonLd(article: ReturnType<typeof findArticleBySlug>, siteUrl: string, canonicalPath: string) {
  if (!article) return undefined;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: "2026-06-01",
    dateModified: article.updatedDate,
    author: {
      "@type": "Organization",
      name: "Superbuy Study",
      url: siteUrl,
    },
    publisher: {
      "@type": "Organization",
      name: "Superbuy Study",
      url: siteUrl,
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${siteUrl.replace(/\/$/, "")}${canonicalPath}`,
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: article.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a,
      },
    })),
  };

  return [articleSchema, faqSchema];
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  const effectiveSlug = slug || "superbuy-spreadsheet-guide";
  const article = findArticleBySlug(effectiveSlug);
  const canonicalPath = slug ? `/article/${slug}` : "/article/superbuy-spreadsheet-guide";

  const siteUrl = useMemo(() => {
    try {
      return import.meta.env.VITE_SITE_URL || "https://superbuy.study";
    } catch {
      return "https://superbuy.study";
    }
  }, []);

  const seoData = useMemo(() => {
    if (!article) {
      return {
        title: "Article Not Found - Superbuy Study",
        description: "The article you are looking for does not exist or has been moved.",
        keywords: "superbuy spreadsheet",
        jsonLd: undefined as ReturnType<typeof buildArticleJsonLd>,
      };
    }

    const kwBase = "superbuy spreadsheet";
    const titleSuffix = " | Superbuy Study";
    const kwFromCategory = article.category.toLowerCase().replace(/\s+/g, ", ");
    return {
      title: `${article.title}${titleSuffix}`,
      description: article.excerpt,
      keywords: `${kwBase}, ${kwFromCategory}, ${article.slug.replace(/-/g, " ")}`,
      jsonLd: buildArticleJsonLd(article, siteUrl, canonicalPath),
    };
  }, [article, siteUrl, canonicalPath]);

  if (!article) {
    return (
      <>
        <SEOHead
          title={seoData.title}
          description={seoData.description}
          keywords={seoData.keywords}
          canonicalPath={canonicalPath}
          noIndex
        />
        <Navbar />
        <main className="pt-24 pb-16 bg-background-50 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-semibold text-foreground-950 mb-3">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist or has been moved.</p>
            <Link to="/" className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer">
              <i className="ri-home-4-line"></i> Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedTitles(article.relatedSlugs);

  return (
    <>
      <SEOHead
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonicalPath={canonicalPath}
        ogType="article"
        ogImage={article.imageUrl}
        jsonLd={seoData.jsonLd}
      />
      <Navbar />
      <main className="pt-24 pb-16 bg-background-50">
        <article className="max-w-3xl mx-auto px-6 md:px-10">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors underline underline-offset-4">Home</Link>
            <span>/</span>
            <span className="text-foreground-600">{article.category}</span>
            <span>/</span>
            <span className="text-foreground-600 truncate max-w-[200px]">{article.title}</span>
          </nav>

          {/* Header */}
          <header className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2.5 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                {article.category}
              </span>
              <span className="text-xs text-foreground-400">Updated {article.updatedDate}</span>
              <span className="text-xs text-foreground-400">· {article.readTime}</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              {article.subtitle}
            </p>
          </header>

          {/* Featured Image */}
          <div className="mb-10 rounded-2xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-auto object-cover"
            />
          </div>

          {/* Table of Contents */}
          <nav className="bg-white rounded-xl p-5 md:p-6 border border-background-200/70 mb-10">
            <h2 className="text-sm font-semibold text-foreground-900 uppercase tracking-wide mb-3">Table of Contents</h2>
            <ol className="space-y-1.5 text-sm text-foreground-600">
              {article.sections.map((section, idx) => (
                <li key={section.id}>
                  <a href={`#${section.id}`} className="hover:text-primary-600 transition-colors underline underline-offset-4">
                    {idx + 1}. {section.title}
                  </a>
                </li>
              ))}
              <li>
                <a href="#faq" className="hover:text-primary-600 transition-colors underline underline-offset-4">
                  {article.sections.length + 1}. Frequently Asked Questions
                </a>
              </li>
            </ol>
          </nav>

          {/* Sections */}
          {article.sections.map((section) => {
            if (section.type === "table" && section.tableHeaders && section.tableRows) {
              return (
                <section key={section.id} id={section.id} className="mb-10">
                  <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-4">
                    {section.title}
                  </h2>
                  {section.paragraphs.length > 0 && (
                    <div className="prose-custom space-y-4 text-foreground-700 leading-relaxed text-sm md:text-base mb-4">
                      {section.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
                    </div>
                  )}
                  <div className="overflow-x-auto rounded-xl border border-background-200/70">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-background-100">
                          {section.tableHeaders.map((h, i) => (
                            <th key={i} className="text-left px-4 py-3 font-semibold text-foreground-900 whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {section.tableRows.map((row, ri) => (
                          <tr key={ri} className={`border-t border-background-200/70 ${ri % 2 === 1 ? "bg-background-50/50" : ""}`}>
                            {row.map((cell, ci) => (
                              <td key={ci} className={`px-4 py-3 ${ci === 0 ? "font-medium text-foreground-800" : "text-foreground-600"}`}>{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              );
            }

            if (section.type === "pros-cons" && section.pros && section.cons) {
              return (
                <section key={section.id} id={section.id} className="mb-10">
                  <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-4">
                    {section.title}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl p-5 border border-background-200/70">
                      <h3 className="flex items-center gap-2 text-base font-semibold text-primary-700 mb-3">
                        <i className="ri-checkbox-circle-line text-lg"></i> Pros
                      </h3>
                      <ul className="space-y-2 text-sm text-foreground-700">
                        {section.pros.map((p, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-primary-500 mt-0.5 shrink-0"><i className="ri-check-line"></i></span>
                            {p}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-white rounded-xl p-5 border border-background-200/70">
                      <h3 className="flex items-center gap-2 text-base font-semibold text-accent-700 mb-3">
                        <i className="ri-error-warning-line text-lg"></i> Cons
                      </h3>
                      <ul className="space-y-2 text-sm text-foreground-700">
                        {section.cons.map((c, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-accent-500 mt-0.5 shrink-0"><i className="ri-close-line"></i></span>
                            {c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </section>
              );
            }

            if (section.type === "cta") {
              return (
                <section key={section.id} id={section.id} className="mb-10">
                  <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-4">
                    {section.title}
                  </h2>
                  <div className="prose-custom space-y-4 text-foreground-700 leading-relaxed text-sm md:text-base mb-4">
                    {section.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
                  </div>
                  <div className="bg-primary-50 border border-primary-200/60 rounded-xl p-6">
                    <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">Ready to Start Shopping Smarter?</h3>
                    <p className="text-sm text-foreground-600 mb-4">
                      Explore our comprehensive product spreadsheets organized across 14 categories. Find exactly what you are looking for with verified products, quality ratings, and direct purchase links.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <a
                        href="https://kaspreadsheet.com/"
                        target="_blank"
                        rel="noopener nofollow"
                        className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer"
                      >
                        <i className="ri-shopping-cart-2-line"></i>
                        Browse Spreadsheets
                      </a>
                      <Link
                        to="/"
                        className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-primary-300 text-primary-700 font-semibold text-sm hover:bg-primary-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-home-4-line"></i>
                        Back to Home
                      </Link>
                    </div>
                  </div>
                </section>
              );
            }

            return (
              <section key={section.id} id={section.id} className="mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-4">
                  {section.title}
                </h2>
                <div className="prose-custom space-y-4 text-foreground-700 leading-relaxed text-sm md:text-base">
                  {section.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </section>
            );
          })}

          {/* Safety Notice for safety article */}
          {article.slug === "is-superbuy-safe" && (
            <div className="bg-accent-50 border border-accent-200/60 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <span className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-500 text-white shrink-0 mt-0.5">
                  <i className="ri-shield-check-line text-sm"></i>
                </span>
                <div>
                  <h4 className="font-semibold text-foreground-900 mb-1">Educational Purpose Notice</h4>
                  <p className="text-sm text-foreground-600">
                    This website is an independent educational resource and is not affiliated with Superbuy or any of its associated brands. The information provided here is for informational purposes only. Users should conduct their own independent research and exercise personal judgment before making any purchasing decisions.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* FAQ */}
          <section id="faq" className="mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-6">
              Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {article.faqs.map((item, index) => (
                <details key={index} className="group bg-white rounded-xl border border-background-200/70 overflow-hidden">
                  <summary className="px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-background-50 transition-colors list-none">
                    <span className="text-sm md:text-base font-semibold text-foreground-900 pr-4">{item.q}</span>
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 shrink-0 group-open:rotate-45 transition-transform duration-200">
                      <i className="ri-add-line text-sm"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <div className="bg-white rounded-xl p-5 border border-background-200/70 mb-10">
              <h3 className="text-sm font-semibold text-foreground-900 mb-3">Continue Reading</h3>
              <div className="flex flex-wrap gap-2">
                <Link to="/" className="px-3 py-1.5 rounded-full bg-background-100 text-xs font-medium text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer">
                  ← Home
                </Link>
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="px-3 py-1.5 rounded-full bg-background-100 text-xs font-medium text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer line-clamp-1 max-w-[180px]"
                  >
                    {ra.title}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}